import {
  Css,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  HeaderAction,
  IconButton,
  ModalBody,
  ModalHeader,
  SelectToggle,
  Tag,
  column,
  multiFilter,
  selectColumn,
  simpleDataRows,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useMemo, useState } from "react";
import { PdfViewer } from "src/components";
import { lienWaiverStatusTagMapper } from "src/components/detailPane/bill/utils";
import {
  BillPage_LienWaiverFragment,
  DocumentEditorDetailFragment,
  LienWaiversPageLienWaiverFragment,
} from "src/fragments/circularFragments.generated";
import { DocumentType, LienWaiverFilter, LienWaiverOrder, Order } from "src/generated/graphqlTypes";
import { FinancesPageSharedQuery } from "src/routes/billing/FinancesPage.generated";
import { LienWaiversPageQuery, useLienWaiversPageQuery } from "src/routes/lien-waivers/LienWaiversPage.generated";
import { useDocumentsAssetQuery } from "src/routes/projects/documents/DocumentsPage.generated";
import { formatList } from "src/utils";
import { downloadFilesToZip } from "src/utils/downloader";
import { parseOrder, toOrder } from "src/utils/ordering";
import { openNewTab } from "src/utils/window";
import { financesDefaultPage } from "../billing/FinancesPage";
import { UploadLienWaiverModal } from "../bills/components/UploadLienWaiverModal";
import { PandaDocConfirmationModal } from "../development-commitments/components/PandaDocConfirmationModal";
import { CreateLienWaiverModal } from "./CreateLienWaiverModal";

type LienWaiversPageFilter = LienWaiverFilter;

type Row = { kind: "header" } | { kind: "data"; id: string; data: LienWaiversPageLienWaiverFragment };

type LienWaiversPageProps = { shared: FinancesPageSharedQuery };

export function LienWaiversPage({ shared }: LienWaiversPageProps) {
  const { markets, projects, tradePartners } = shared;
  const [order, setOrder] = useState<LienWaiverOrder>({ id: Order.Desc });
  const { openModal, closeModal } = useModal();
  const tableApi = useGridTableApi<Row>();

  const filterDefs: FilterDefs<LienWaiversPageFilter> = useMemo(
    () => ({
      market: multiFilter({
        label: "Market",
        options: markets,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      project: multiFilter({
        label: "Project",
        options: projects,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      tradePartner: multiFilter({
        label: "Trade Partner",
        options: tradePartners,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
    }),
    [markets, projects, tradePartners],
  );
  const layoutState = useGridTableLayoutState<LienWaiversPageFilter>({
    persistedFilter: { filterDefs, storageKey: "financesLienWaiverFilter" },
    search: "server",
  });
  const query = useLienWaiversPageQuery({
    variables: {
      filter: { ...layoutState.filter, search: layoutState.searchString ?? "" },
      order,
      page: financesDefaultPage,
    },
  });
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.lienWaiversPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.lienWaiversPage.entities.length, limit: financesDefaultPage.limit } },
      });
    }
  }, [query]);
  const selectedRows = useComputed(() => tableApi.getSelectedRows("data").map((row) => row.data), [tableApi]);
  const documentIds = selectedRows.filter(({ document }) => document).map(({ document }) => document!.id);
  const documentAssetQuery = useDocumentsAssetQuery({ variables: { filter: { id: documentIds } } });
  const lienWaiversForDownload = documentAssetQuery.data?.documents;
  const columns = useMemo(() => getColumns(), []);
  const createRows = useCallback(
    (data: LienWaiversPageQuery | undefined) => simpleDataRows(data?.lienWaiversPage.entities ?? []),
    [],
  );
  const actions: HeaderAction[] = useMemo(
    () => [
      {
        kind: "menu",
        trigger: { icon: "verticalDots", variant: "outline" },
        items: [
          {
            label: "Create Final Lien Waiver",
            onClick: () =>
              openModal({
                content: (
                  <CreateLienWaiverModal tradePartners={tradePartners} projects={projects} closeModal={closeModal} />
                ),
              }),
          },
          {
            label: "Download .zip File",
            disabled: documentIds.isEmpty,
            onClick: () => {
              void downloadFilesToZip(
                "Lien_Waivers.zip",
                lienWaiversForDownload!.map(({ name, asset }) => ({
                  filename: asset.fileName ?? name,
                  url: asset.downloadUrl,
                })),
              );
            },
          },
        ],
      },
    ],
    [closeModal, documentIds.isEmpty, lienWaiversForDownload, openModal, projects, tradePartners],
  );

  return (
    <GridTableLayout
      hideEditColumns
      layoutState={layoutState}
      actions={actions}
      tableProps={{
        api: tableApi,
        query,
        createRows,
        columns,
        sorting: {
          on: "server",
          onSort: (key, direction) => setOrder(toOrder(key, direction)),
          value: parseOrder(order),
        },
        as: "virtual",
        style: { rowHeight: "fixed" },
        emptyFallback: "No lien waivers found that matched given filters.",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

function getColumns(): GridColumn<Row>[] {
  return [
    selectColumn<Row>({
      data: (data, { row }) => ({ content: () => <SelectToggle disabled={!data.document} id={row.id} /> }),
    }),
    column<Row>({
      header: "Project",
      data: ({ project }) => project.name,
      serverSideSortKey: "project",
      w: 2,
      mw: "180px",
    }),
    column<Row>({
      header: "Trade Partner",
      data: ({ claimant }) => (claimant.__typename === "TradePartner" ? claimant.name : "-"),
      w: 2,
      mw: "160px",
    }),
    column<Row>({
      header: "Bill Number",
      data: ({ parent }) => (parent.__typename === "Bill" ? parent.tradePartnerNumber : "-"),
      mw: "120px",
    }),
    column<Row>({ header: "Type", data: ({ type }) => type.name, mw: "100px" }),
    column<Row>({ header: "Condition", data: ({ condition }) => condition.name, mw: "130px" }),
    column<Row>({
      header: "Status",
      data: (row) => {
        const [billType, text] = lienWaiverStatusTagMapper(row.status.code);
        return <Tag type={billType} text={text} />;
      },
      mw: "100px",
    }),
    column<Row>({
      header: "",
      data: (row) => ({
        content: (
          <>
            <LienWaiverDocumentGridCell lw={row} />
            <LienWaiverResendGridCell lw={row} />
            <LienWaiverUploadGridCell lw={row} />
          </>
        ),
      }),
      mw: "120px",
    }),
  ];
}

type LienWaiverGridCellProps = { lw: LienWaiversPageLienWaiverFragment | BillPage_LienWaiverFragment };

export function LienWaiverDocumentGridCell({ lw }: LienWaiverGridCellProps) {
  const { openModal } = useModal();
  const { document } = lw;
  const isDisabled = !document && !lw.pandaDoc?.externalPandaDocUrl;

  return (
    <IconButton
      data-testid="lien-waiver-doc"
      icon="document"
      tooltip={isDisabled ? "Not uploaded" : "View Lien Waiver"}
      onClick={() =>
        document
          ? openModal({ content: <LienWaiverDocumentModal document={document} />, size: "xxl" })
          : openNewTab(lw.pandaDoc?.externalPandaDocUrl!)
      }
      disabled={isDisabled}
    />
  );
}

export function LienWaiverResendGridCell({ lw }: LienWaiverGridCellProps) {
  const { openModal } = useModal();

  return (
    <IconButton
      data-testid="lien-waiver-resend"
      icon="email"
      tooltip="Resend Lien Waiver via PandaDoc"
      onClick={async () => {
        openModal({ content: <PandaDocConfirmationModal ownerId={lw.id} documentName="lien waiver" isReUpload /> });
      }}
      disabled={
        !lw.canCreatePandaDoc.allowed
          ? formatList(lw.canCreatePandaDoc.disabledReasons.map((r) => r.message))
          : undefined
      }
    />
  );
}

export function LienWaiverUploadGridCell({ lw }: LienWaiverGridCellProps) {
  const { openModal } = useModal();

  return (
    <IconButton
      data-testid="lien-waiver-upload"
      icon="cloudUpload"
      tooltip="Upload Lien Waiver from computer"
      onClick={() => {
        openModal({
          content: (
            <UploadLienWaiverModal ownerId={lw.id} parentId={lw.project.id} documentType={DocumentType.LienWaiver} />
          ),
        });
      }}
    />
  );
}

function LienWaiverDocumentModal({ document }: { document: DocumentEditorDetailFragment }) {
  return (
    <div>
      <ModalHeader>Lien Waiver Document</ModalHeader>
      <ModalBody>
        <div css={Css.bgWhite.bshBasic.oa.br8.mb3.$}>
          <PdfViewer hasHeader assets={[document?.asset]} title={""} />
        </div>
      </ModalBody>
    </div>
  );
}
